import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import CTPopupMenu from "./CTPopupMenu";
import Icono from "./CTIcons";
import CTCell from "components/CTCell";
import CTImage from "components/CTImage";
import { strhor, ctod } from "lib/dates";
import { intToCssColor } from "lib/color";

// Componente interno para manejar imágenes con revocación de objectURL
const TableImage = ({ blob, alt = "", width = "w-20", height = "h-20" }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setSrc(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      className={`${width} ${height} object-contain rounded`}
    />
  );
};

const CTTable = ({
  id,
  data,
  columns,
  getRowId = (row) => row.ID,
  draggable = false,
  onRowClick,
  onRowDoubleClick,
  onDragStart,
  onDragOver,
  onDrop,
  contextMenuOptions = [],
  onContextMenuSelect,
  onLoadMore,
  hasMore = false,
  loading = false,
  defaultIcon = -1,
  getRowClass = () => "",
  getCellClass = () => "",
  getRowStyle = () => ({}),
  getIcon = () => -1,
  scrollHorizontal = true,
  showCellBorders = true,
  cellBorderColor = "border-gray-300 dark:border-gray-700",
  cellBorderStyle = "border-dotted",
  cellBorderWidth = "border",
  hoverEffect = true,
  pijama = false,
  selectedRowId: externalSelectedRowId,
  className="",
  rowHeight = "auto", // "auto" | "compact" | "cozy" | "tall"
  toggleRow = () => {}, // 👈 función vacía por defecto // 👈 necesario para que funcione el checkbox por fila
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const observer = useRef();

  const isEmpty = data.length === 0;

  //  Detectar móvil
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
  }, []);


  
  useEffect(() => {
    if (externalSelectedRowId !== undefined)
      setSelectedRowId(externalSelectedRowId);
  }, [externalSelectedRowId]);

  // Cache de imágenes
  const imageCache = useRef(new Map());

  const lastRowRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) onLoadMore?.();
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, onLoadMore]
  );

  const handleEmptyDrop = (e) => {
    e.preventDefault();
    if (draggable && onDrop) onDrop(e, 0);
  };

  const handleContextMenu = (e, rowData) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, rowData });
  };

  const handleMenuSelect = (option) => {
    if (option.onClick) option.onClick(contextMenu?.rowData);
    else onContextMenuSelect?.(option, contextMenu?.rowData);
    setContextMenu(null);
  };
  const handleRowSelect = (row, rowId) => {
    setSelectedRowId(rowId);
    if (onRowClick) onRowClick(row, rowId);
  };

  // 2️⃣ Botón ⋮ handler
  const handleMenuButtonClick = (e, rowData) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      x: rect.left,
      y: rect.bottom,
      rowData,
    });
  };

  // Preprocesar colores tipo 11
  const processedData = useMemo(() => {
    return data.map((row) => {
      const newRow = { ...row };
      columns.forEach((col) => {
        if (col.type === 11 && row[col.accessor] != null) {
          newRow[`__color_${col.accessor}`] = intToCssColor(row[col.accessor]);
        }
      });
      return newRow;
    });
  }, [data, columns]);

  // Renderizado de celdas con formateo
  const renderCellContent = (row, col, colIndex) => {
    const value = row[col.accessor];
    const iconNumber = getIcon(row, col, colIndex) ?? -1;

    if (col.type === 9 && value)
      return <Icono name={String(value)} size={20} />;

    if (col.type === 8 && value) {
      // Si no está en cache, guardamos el blob
      if (!imageCache.current.has(value)) {
        imageCache.current.set(value, value); // value = Blob o URL
      }
      return (
        <TableImage blob={imageCache.current.get(value)} alt={col.header} />
      );
    }

    if (col.type === 5 && value != null) {
      if (!Number(value)) return "";
      return new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: col.dec,
        maximumFractionDigits: col.dec,
        useGrouping: true, // fuerza el separador de miles
      }).format(Number(value));
    }
    if (col.type === 4 && value) {
      if (col.typefield === 13) return ctod(value);
      const d = new Date(value);
      return (
        d.toLocaleDateString("es-ES").replace(/\//g, "-") +
        " " +
        d.toLocaleTimeString("es-ES").replace(/\//g, "-")
      );
    }
    if (col.type === 10 && value != null) return strhor(value);

    // Si es tipo 11, devolver el valor normalmente pero la celda se pinta
    if (col.type === 11 && value != null) return "";

    // checkbox por fila
    if (col.type === "check") {
      return (
        <input
          type="checkbox"
          checked={!!row[col.accessor]}
          onChange={() => {
            // si pasamos toggleRow desde props, lo usamos
            if (toggleRow) toggleRow(row);
          }}
        />
      );
    }

    if (colIndex === 0) {
      const iconToRender = iconNumber !== -1 ? iconNumber : defaultIcon;
      return (
        <span className="inline-flex items-center gap-2">
          {iconToRender !== -1 && (
            <Icono name={String(iconToRender)} size={20} />
          )}
          {value ?? ""}
        </span>
      );
    }

    return value ?? "";
  };

  // Liberar cache de imágenes al desmontar
  useEffect(() => {
    return () => {
      imageCache.current.clear();
    };
  }, []);

  const cellBorderClasses = showCellBorders
    ? `${cellBorderWidth} ${cellBorderStyle} ${cellBorderColor}`
    : "";

  // Tamaños de fila tipo Fiori
  const rowHeights = {
    compact: "h-8", // ~32px
    cozy: "h-12", // ~48px
    tall: "h-16", // ~64px
    auto: "h-auto", // ajusta al contenido
  };
  const rowHeightClass = rowHeights[rowHeight] || "h-auto";
  // Funció helper per a classes
  const getSafeRowClass = (row, isSelected) => {
    try {
      return getRowClass(row, isSelected) || "";
    } catch (e) {
      console.error("Error en getRowClass:", e);
      return "";
    }
  };

  return (
    <div
      className={`cttable-container select-none ${
        scrollHorizontal ? "overflow-x-auto" : "overflow-x-hidden"
      }`}
      onDragOver={draggable ? onDragOver : undefined}
      onDrop={draggable && isEmpty ? handleEmptyDrop : undefined}
    >
      <table
        id={id}
        className={`cttable w-full ${
          scrollHorizontal ? "table-auto" : "table-fixed"
        } 
           
                  ${hoverEffect ? "cttable-hover" : ""} 
                  ${pijama ? "cttable-stripe" : ""}
                     ${className} `
                
                }

      >
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th
                key={idx}
                style={{
                  ...(col.LLARGEDIT ? { width: `${col.LLARGEDIT}rem` } : {}),
                  maxWidth: "60rem", // 👈 límite duro
                  overflow: "hidden", // 👈 evita desbordamiento visual
                  textOverflow: "ellipsis", // 👈 puntos suspensivos si el texto no cabe
                  whiteSpace: "nowrap", // 👈 evita salto de línea
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isEmpty ? (
            <tr
              className="cttable-empty"
              onContextMenu={(e) => handleContextMenu(e, null)}
            >
              <td colSpan={columns.length}>
                {draggable
                  ? "Arrastra aquí elementos para agregarlos"
                  : "No hay datos"}
              </td>
            </tr>
          ) : (
            processedData.map((row, index) => {
              const rowId = getRowId(row);

              const isSelected = rowId === selectedRowId;
              const isLastRow = index === data.length - 1;

              const defaultRowStyle = getRowStyle(row, isSelected);
              const className = `${
                isSelected ? "cttable-selected" : ""
              } ${getSafeRowClass(row, isSelected)}`.trim();
              return (
                <tr
                  key={rowId}
                  ref={isLastRow ? lastRowRef : null}
                  className={`${className} ${rowHeightClass} `}
                  style={defaultRowStyle}
                  draggable={draggable}
                  onClick={() => handleRowSelect(row, rowId)}
                  onDoubleClick={(e) => onRowDoubleClick?.(row, e)}
                  onContextMenu={(e) => handleContextMenu(e, row)}
                  onDragStart={
                    draggable ? (e) => onDragStart?.(e, index) : undefined
                  }
                  onDragOver={draggable ? onDragOver : undefined}
                  onDrop={draggable ? (e) => onDrop?.(e, index) : undefined}
                >
                  {columns.map((col, colIndex) => (
                    <td
                      key={`${rowId}-${col.accessor}`}
                      style={{
                        ...(col.LLARGEDIT
                          ? { width: `${col.LLARGEDIT}rem` }
                          : {}),
                        ...(col.type === 11
                          ? { backgroundColor: row[`__color_${col.accessor}`] }
                          : {}),
                        maxWidth: "50rem",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <CTCell
                        row={row}
                        col={col}
                        colIndex={colIndex}
                        getIcon={getIcon}
                        defaultIcon={defaultIcon}
                        toggleRow={toggleRow}
                        loadImage={col.loadImage} // opcional si defines función para imágenes
                      />
                    </td>
                  ))}
                  {isMobile && contextMenuOptions.length > 0 && (
                    <td className="text-right pr-2">
                      <button
                        onClick={(e) => handleMenuButtonClick(e, row)}
                        className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      >
                        ⋮
                      </button>
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {contextMenu && (
        <CTPopupMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenuOptions}
          onSelect={handleMenuSelect}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

// Agrega al final de tu CTTable.jsx, antes del export default si quieres
CTTable.propTypes = {
  id: PropTypes.string,
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      accessor: PropTypes.string,
      header: PropTypes.node,
      type: PropTypes.number,
      typefield: PropTypes.number,
      dec: PropTypes.number,
      LLARGEDIT: PropTypes.number,
      className: PropTypes.string,
    })
  ).isRequired,
  getRowId: PropTypes.func,
  draggable: PropTypes.bool,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragOver: PropTypes.func,
  onDrop: PropTypes.func,
  contextMenuOptions: PropTypes.array,
  onContextMenuSelect: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loading: PropTypes.bool,
  defaultIcon: PropTypes.number,
  getRowClass: PropTypes.func,
  getCellClass: PropTypes.func,
  getRowStyle: PropTypes.func,
  getIcon: PropTypes.func,
  scrollHorizontal: PropTypes.bool,
  showCellBorders: PropTypes.bool,
  cellBorderColor: PropTypes.string,
  cellBorderStyle: PropTypes.string,
  cellBorderWidth: PropTypes.string,
  hoverEffect: PropTypes.bool,
  pijama: PropTypes.bool,
  rowHeight: PropTypes.oneOf(["auto", "compact", "cozy", "tall"]),
};

CTTable.defaultProps = {
  id: undefined,
  getRowId: (row) => row.ID,
  draggable: false,
  contextMenuOptions: [],
  hasMore: false,
  loading: false,
  defaultIcon: -1,
  getRowClass: () => "",
  getCellClass: () => "",
  getRowStyle: () => ({}),
  getIcon: () => -1,
  scrollHorizontal: true,
  showCellBorders: true,
  cellBorderColor: "border-gray-300 dark:border-gray-700",
  cellBorderStyle: "border-dotted",
  cellBorderWidth: "border",
  hoverEffect: true,
  pijama: false,
  rowHeight: "auto",
};
export default CTTable;
